import { defineStore } from 'pinia'
import ClientService from '@/api/ClientService.js'

const defaultClientData = () => ({
    id: null,
    is_dummy: false,
    name: '',
    vat_identification_number: '',
    company_registration_number: '',
    duns: 0,
    web_url: '',
    web_url_missing: false,
    country_id: '',

    street: '',
    postal_code: '',
    city: '',
    contact_person: '',
    contact_email: '',

    invoice_emails: [],
    maintenance_emails: [],
    maintenance_email: '',

    phone: '',
    authorized_people: [],
    removable: false,
    dnb_pd: 1,
    dnb_pd_date: null,
    dnb_transaction_timestamp: null,
    dnb_report_json: [],
    dnb_created_at: '',
    dnb_company_financials: [],
    dnb_credit_risk: [],
    dnb_pd_dates: [],
    dnb_report_dates: [],
    business_relationship: '',
    memorandum_provided: false,
    latest_corporate_registry_file: null,
    country_name: '',
    country_high_risk: false,
})

export const useClientStore = defineStore('client', {
    state: () => ({
        clientData: defaultClientData(),
        savedClientSnapshot: null,
        clientRequestId: 0,

        isValidBasicInfo: false,
        isValidAdditionalInfo: false,
        isValidContactInfo: false,
        loading: false,
        saving: false,
        fetchingDnB: false,
        searchingDnb: false,
        dnbMissingFields: [],
        complianceReportsVersion: 0,
        dummyContracts: [],
    }),

    getters: {
        isValid: (state) => {
            return state.isValidBasicInfo && state.isValidAdditionalInfo && state.isValidContactInfo
        },

        hasUnsavedChanges: (state) => state.savedClientSnapshot !== JSON.stringify(state.clientData),
    },

    actions: {
        notification(severity, detail) {
            const summary = severity === 'error' ? 'Error' : 'Success'
            this.toast.add({
                severity,
                summary,
                detail,
                life: this.getToastDuration(detail, severity),
            })
        },

        resetState() {
            this.clientData = defaultClientData()
            this.savedClientSnapshot = null
            this.clientRequestId++
            this.loading = false

            this.isValidBasicInfo = false
            this.isValidAdditionalInfo = false
            this.isValidContactInfo = false
            this.dnbMissingFields = []
            this.dummyContracts = []
        },

        async getClientData(client) {
            const requestId = ++this.clientRequestId
            this.loading = true
            this.dummyContracts = []
            try {
                const data = await ClientService.get(client)
                if (requestId !== this.clientRequestId) return
                Object.keys(data)
                    .filter((key) => key !== 'locales')
                    .forEach((key) => {
                        if (this.clientData[key] !== undefined) {
                            this.clientData[key] = data[key]
                        }
                    })
                this.savedClientSnapshot = JSON.stringify(this.clientData)
                if (data.is_dummy === true) {
                    this.fetchDummyContracts(client, requestId)
                }
            } catch (error) {
                if (requestId !== this.clientRequestId) return
                console.error('Error fetching client data (stores/client/getClientData):', error)
            } finally {
                if (requestId === this.clientRequestId) this.loading = false
            }
        },

        async discardUnsavedChanges() {
            const id = this.clientData.id
            if (!id) {
                this.resetState()
                return
            }
            if (!this.hasUnsavedChanges) {
                return
            }
            this.resetState()
            this.clientData.id = id
            await this.getClientData(id)
        },

        async fetchDummyContracts(client, requestId = this.clientRequestId) {
            try {
                const { data } = await ClientService.contracts({ client })
                if (requestId !== this.clientRequestId) return
                this.dummyContracts = Object.entries(data).map(([id, uid]) => ({ id, uid }))
            } catch (error) {
                console.error(
                    'Error fetching placeholder contracts (stores/client/fetchDummyContracts):',
                    error
                )
            }
        },

        async create(fromCafModal = false) {
            this.saving = true
            try {
                const { data } = await ClientService.create(this.clientData)
                this.clientData.id = data.client_id
                if (!fromCafModal) {
                    this.router.push({ name: 'ClientShow', params: { id: this.clientData.id } })
                }
                this.notification('success', '§ui_message.create_successful')
            } catch (error) {
                console.error('Error creating client (stores/client/create):', error)
                const serverMessage = error.response?.data?.message
                const text = serverMessage || '§ui_message.create_failed'
                this.notification('error', text)
            } finally {
                this.saving = false
            }
        },

        async update() {
            this.saving = true
            try {
                await ClientService.update({ client: this.clientData.id }, this.clientData)
                this.savedClientSnapshot = JSON.stringify(this.clientData)
                this.notification('success', '§ui_message.update_successful')

                return true
            } catch (error) {
                console.error('Error updating client data (stores/client/update):', error)
                const serverMessage = error.response?.data?.message
                const text = serverMessage || '§ui_message.update_failed'
                this.notification('error', text)

                return false
            } finally {
                this.saving = false
            }
        },

        async delete() {
            this.saving = true
            try {
                await ClientService.delete({ client: this.clientData.id })
                this.resetState()
                this.router.push({ name: 'ClientIndex' })
                this.notification('success', '§ui_message.delete_successful')
            } catch (error) {
                console.error('Error deleting client (stores/client/delete):', error)
                this.notification('error', '§ui_message.delete_failed')
            } finally {
                this.saving = false
            }
        },

        async dnbSearch(search) {
            this.searchingDnb = true
            try {
                const { data } = await ClientService.dnbSearch(search)
                return data
            } catch (error) {
                console.error('Error searching DNB (stores/client/dnbSearch):', error)
                this.notification('error', '§ui_message.dnb_search_failed')
                return []
            } finally {
                this.searchingDnb = false
            }
        },

        async lookupByDuns(duns) {
            try {
                const { data } = await ClientService.lookupByDuns(duns)
                return data
            } catch (error) {
                console.error('Error looking up DUNS (stores/client/lookupByDuns):', error)
                this.notification('error', '§ui_message.dnb_lookup_failed')
                return null
            }
        },

        async fetchDnB(force = false) {
            if (this.fetchingDnB) return
            this.fetchingDnB = true
            try {
                const { data } = await ClientService.fetchDnB({ client: this.clientData.id, force })
                this.clientData.dnb_report_json = data.dnb_report_json
                this.clientData.dnb_pd = data.dnb_pd
                this.clientData.dnb_pd_date = data.dnb_pd_date
                this.clientData.dnb_transaction_timestamp = data.dnb_transaction_timestamp
                this.clientData.dnb_created_at = data.dnb_created_at
                this.clientData.dnb_company_financials = data.dnb_company_financials
                this.clientData.dnb_credit_risk = data.dnb_credit_risk
                this.notification('success', '§ui_message.dnb_successful')
            } catch (error) {
                console.error('Error fetching DnB data (stores/client/fetchDnB):', error)
                this.notification('error', '§ui_message.dnb_failed')
            } finally {
                this.fetchingDnB = false
            }
        },

        async fetchCreditRiskByReport(reportId) {
            try {
                const { data } = await ClientService.fetchCreditRiskByReport({
                    client: this.clientData.id,
                    dnb_report: reportId,
                })
                this.clientData.dnb_credit_risk = data
            } catch (error) {
                console.error('Error fetching credit risk by report:', error)
                this.notification('error', '§ui_message.dnb_failed')
            }
        },

        async fetchStandardDataByReport(reportId) {
            try {
                const { data } = await ClientService.fetchStandardDataByReport({
                    client: this.clientData.id,
                    dnb_report: reportId,
                })
                this.clientData.dnb_report_json = data.dnb_report_json
                this.clientData.dnb_transaction_timestamp = data.dnb_transaction_timestamp
            } catch (error) {
                console.error('Error fetching standard data by report:', error)
                this.notification('error', '§ui_message.dnb_failed')
            }
        },

        addAuthorizedPerson(authorizedPerson) {
            this.$patch((state) => {
                state.clientData.authorized_people.push(authorizedPerson)
            })
        },

        updateAuthorizedPerson(authorizedPerson) {
            const index = this.clientData.authorized_people.findIndex(
                (person) => person.id === authorizedPerson.id
            )
            if (index !== -1) {
                this.clientData.authorized_people[index] = {
                    ...this.clientData.authorized_people[index],
                    ...authorizedPerson,
                }
            }
        },

        incrementComplianceReportsVersion() {
            this.complianceReportsVersion++
        },

        updateAuthorizedPersonRpsData(personIds, rpsData) {
            try {
                this.clientData.authorized_people = this.clientData.authorized_people.map(
                    (person) => {
                        if (person.id === personIds) {
                            return {
                                ...person,
                                rps: { ...rpsData },
                            }
                        }
                        return person
                    }
                )
                this.notification('success', '§ui_message.kyc_data_update_successful')
            } catch (error) {
                console.error(
                    'Error updating KYC data (stores/client/updateAuthorizedPersonRpsData):',
                    error
                )
                this.notification('error', '§ui_message.kyc_data_update_failed')
            }
        },
    },
})
//
